import classNames from 'classnames';
import { kebabCase } from 'lodash';
import numeral from 'numeral';
import React, { Fragment } from 'react';
import { DisplayClassification, Fund } from 'src/components/Exchange/types';
import Checkbox from 'src/components/Shared/Checkbox/Checkbox';
import If from 'src/components/Shared/If/If';
import Note from 'src/components/Shared/Note/Note';
import RecurringFlag from 'src/components/Shared/RecurringFlag/RecurringFlag';
import ToolTip from 'src/components/Shared/ToolTip/ToolTip';
import {
  AGREEMENT_TYPES,
  AUTOMATIC_SELL_TRANSACTION,
  ExchangeType,
} from 'src/settings/settings';
import { getCurrentYear } from 'src/utils/dates/date-utility';
import { isProductType529 } from 'src/utils/funds/funds-utility';
import { isAPP, isRAS } from 'src/utils/advice/advice-utility';
import {
  BUY_TRANSACTION,
  APP_BUY_TRANSACTION,
  APP_SELL_TRANSACTION,
  COLLEGE_CONTRIBUTE_TRANSACTION,
  COLLEGE_DISTRIBUTE_TRANSACTION,
  COLLEGE_EXCHANGE_TRANSACTION,
  EXCHANGE_TRANSACTION,
  isRecurringBuyEdit,
  isAutomaticSellEdit,
  RECURRING_BUY_TRANSACTION,
  RECURRING_BUY_TRANSACTION_EDIT,
  RECURRING_EXCHANGE_TRANSACTION,
  SELL_TRANSACTION,
  ROTH_CONVERSION,
  transactionType,
  IRA_AUTOMAX_TRANSACTION,
  isBuy,
  isExchange,
  QCD_TRANSACTION,
} from 'src/utils/transactions-util';
import { orEmpty } from 'src/utils/utils';
import FundNotes from '../FundNotes/FundNotes';
import './ExpandedFundHeader.scss';
import { ExpandedFundShares } from './ExpandedFundShares';
import { useSelector } from 'react-redux';
import { getJustPendingAgreementIDs } from '../../../../redux/modules/pendingTransactions/pendingTransactions.selectors';

const valueByTransactionTypes = {
  [BUY_TRANSACTION]: 'balanceAsOf',
  [APP_BUY_TRANSACTION]: 'balanceAsOf',
  [APP_SELL_TRANSACTION]: 'balanceAsOf',
  [COLLEGE_CONTRIBUTE_TRANSACTION]: 'balanceAsOf',
  [EXCHANGE_TRANSACTION]: 'balanceAsOf',
  [SELL_TRANSACTION]: 'equityAsOf',
  [QCD_TRANSACTION]: 'equityAsOf',
  [AUTOMATIC_SELL_TRANSACTION]: 'balanceAsOf',
  [COLLEGE_DISTRIBUTE_TRANSACTION]: 'equityAsOf',
  [COLLEGE_EXCHANGE_TRANSACTION]: 'balanceAsOf',
  [RECURRING_BUY_TRANSACTION]: 'balanceAsOf',
  [IRA_AUTOMAX_TRANSACTION]: 'balanceAsOf',
  [RECURRING_EXCHANGE_TRANSACTION]: 'balanceAsOf',
  [RECURRING_BUY_TRANSACTION_EDIT]: 'balanceAsOf',
  [ROTH_CONVERSION]: 'balanceAsOf',
};

interface ExpandedFundHeaderProps {
  eligibleForRMD: boolean;
  firstOfAgreement;
  fund: Fund;
  fundClassName: string;
  fundDisabled: boolean;
  fundIndex: string;
  fundType: ExchangeType;
  selectedFund: Fund;
  toggleFundChecked(fund: Fund): void;
}

const ExpandedFundHeader: React.FC<ExpandedFundHeaderProps> = ({
  eligibleForRMD,
  firstOfAgreement,
  fund,
  fundClassName,
  fundDisabled,
  fundIndex,
  fundType,
  selectedFund,
  toggleFundChecked,
}) => {
  const {
    agreementNumber,
    agreementType,
    availableShares = 0,
    coveredShares,
    displayClassification,
    formattedEntityNames,
    fundName,
    linkedAgreementNumber,
    newFund,
    productType,
    selected,
    shares,
    sweep,
    agreementID,
    ticker,
    uncoveredShares = 0,
    unfundedNASUAccount = false,
  } = fund;

  const pendingagreementId = useSelector(getJustPendingAgreementIDs);
  const isFundPending = pendingagreementId.some((id) => id === agreementID);
  const pageTransactionType = transactionType();
  const checkFundType = fundType
    ? fundType !== ExchangeType.ONE_TO_MANY
    : false;
  const isRMD = eligibleForRMD;
  const isRetiredFund = selectedFund
    ? selectedFund.displayClassification === DisplayClassification.RETIREMENT
    : false;
  const isRolloverOrTraditionalIRA = selectedFund
    ? selectedFund.agreementType === AGREEMENT_TYPES.TRADITIONAL_IRA ||
      selectedFund.agreementType === AGREEMENT_TYPES.ROLLOVER_IRA
    : false;
  const fundNumberStyle = fund.newFund
    ? 'agreement-number-just'
    : 'account-number';
  return (
    <div
      className={classNames(
        `e2e-${kebabCase(fundName)}`,
        `e2e-fund-row-${agreementNumber}`,
        `e2e-fund-row-${kebabCase(ticker)}`,
        'funds-row',
        {
          'expanded-fund-header-first-of-agreement': firstOfAgreement,
        }
      )}
    >
      <div
        className={classNames(
          'funds-name-col',
          'e2e-checkbox-fund',
          'expanded-fund-header-details',
          { 'expanded-fund-header-editPadding': isRecurringBuyEdit() }
        )}
      >
        <div className="expanded-fund-header-fund-name-first-line">
          {!isRecurringBuyEdit() && !isAutomaticSellEdit() && (
            <>
              <Checkbox
                ariaLabel={`Toggle ${fundName}`}
                checked={selected}
                contextStyle={{ marginTop: '5px' }}
                disabled={fundDisabled}
                e2eSuffix={`fund-${kebabCase(ticker)}`}
                id={`checkbox-${fundClassName}-${fundIndex}`}
                tabIndex={0}
                onClick={() => toggleFundChecked(fund)}
              />
              <a
                className={classNames(
                  'expanded-fund-header-name-container',
                  'e2e-fund-header-name',
                  {
                    'expanded-fund-header-selectable': !fundDisabled,
                    'expanded-fund-header-disabled': fundDisabled,
                  }
                )}
                onClick={() => toggleFundChecked(fund)}
              >
                <span
                  className={classNames(
                    'expanded-fund-header-name',
                    'e2e-fund-name'
                  )}
                >
                  {orEmpty(fundName)}
                </span>
                {!isProductType529(productType) &&
                  !isAPP(productType) &&
                  !isRAS(productType) && (
                    <span
                      className={classNames(
                        'expanded-fund-header-ticker',
                        'e2e-ticker-name'
                      )}
                    >
                      {orEmpty(fund.ticker)}
                    </span>
                  )}
              </a>
            </>
          )}
          <If test={isRecurringBuyEdit() || isAutomaticSellEdit()}>
            <span
              className={classNames(
                'expanded-fund-header-name',
                'e2e-fund-name'
              )}
            >
              {orEmpty(fundName)}
            </span>
            <If
              test={
                !isProductType529(fund.productType) &&
                !isAPP(fund.productType) &&
                !isRAS(productType)
              }
            >
              <span
                className={classNames(
                  'expanded-fund-header-ticker',
                  'e2e-ticker-name'
                )}
              >
                {orEmpty(fund.ticker)}
              </span>
            </If>
          </If>
          {!isAPP(productType) && sweep && (
            <span
              className={classNames(
                'expanded-fund-header-sweep',
                'e2e-fund-sweep'
              )}
              id={`sweep-${fund.ticker}`}
            >
              <span className="expanded-fund-header-sweep_text">Sweep</span>
              <ToolTip
                header="Sweep Account"
                message={
                  <div className="exclude">
                    <p className="exclude">
                      This Money Market fund is linked to your Brokerage Account{' '}
                      <span className="exclude blocked_data text-nowrap">
                        #{linkedAgreementNumber}.
                      </span>
                    </p>
                    <p className="exclude expanded-fund-header-sweep-tip-para">
                      Securities transactions and/or cash distributions from
                      positions held in that specific brokerage account will
                      settle in or out of this "sweep" account.
                    </p>
                  </div>
                }
              />
            </span>
          )}
        </div>
        <div
          className={classNames(
            'expanded-fund-header-second-line',
            `e2e-fund-header-second-line-${kebabCase(fund.ticker)}`,
            { 'expanded-fund-header-selected': selected }
          )}
        >
          <span
            className={`blocked_data text-nowrap expanded-fund-header-${fundNumberStyle}`}
          >
            {newFund ||
            ((isBuy() || isExchange()) &&
              unfundedNASUAccount &&
              !isFundPending) ? (
              <span className="roth-conversion-review_new-fund">
                Just Added
              </span>
            ) : (
              orEmpty(agreementNumber)
            )}
          </span>
          <span
            className="expanded-fund-header-user blocked_data"
            title={formattedEntityNames}
          >
            •{' '}
            <span className="blocked_data">
              {orEmpty(formattedEntityNames)}
            </span>
          </span>
        </div>
        <If
          test={
            checkFundType &&
            !!selected &&
            isRMD &&
            isRetiredFund &&
            isRolloverOrTraditionalIRA &&
            displayClassification !== DisplayClassification.RETIREMENT &&
            parseInt(getCurrentYear('YYYY')) < 2021
          }
        >
          <Note className="expanded-fund-header-exch-rmd-notes" title="Note:">
            The distribution you are requesting may help satisfy all or a
            portion of your required minimum distribution (RMD) for a given
            year. RMDs are waived in 2020 as a result of the CARES Act.
          </Note>
        </If>
      </div>
      <div className="funds-notes-col">
        <FundNotes fund={fund} />
      </div>
      <div className="funds-balance-col">
        {pageTransactionType in valueByTransactionTypes && (
          <Fragment>
            <div className="expanded-fund-header-balance">
              {numeral(
                fund[valueByTransactionTypes[pageTransactionType]] || 0
              ).format('$0,0.00')}
            </div>
            {pageTransactionType !== APP_BUY_TRANSACTION &&
              pageTransactionType !== APP_SELL_TRANSACTION &&
              pageTransactionType !== BUY_TRANSACTION &&
              pageTransactionType !== COLLEGE_CONTRIBUTE_TRANSACTION &&
              pageTransactionType !== RECURRING_BUY_TRANSACTION &&
              pageTransactionType !== RECURRING_BUY_TRANSACTION_EDIT &&
              pageTransactionType !== IRA_AUTOMAX_TRANSACTION &&
              !isAPP(productType) && (
                <ExpandedFundShares
                  {...{
                    agreementType,
                    availableShares,
                    coveredShares,
                    displayClassification,
                    productType,
                    shares,
                    uncoveredShares,
                  }}
                />
              )}
          </Fragment>
        )}
        <RecurringFlag fund={fund} />
      </div>
    </div>
  );
};

export default ExpandedFundHeader;
